import React from 'react';

/* --- Gladiolus-style flower stem inspired by the Erica Thayer logo:
       tall bud spike + face-on 5-petal lavender blooms with radiating stamens
       and sword-shaped leaves at the base --- */
export const Gladiolus = ({ className = '', flip = false }) => {
  const uid = React.useId().replace(/:/g, '');
  const petal = `url(#petG-${uid})`;
  const petalDeep = `url(#petD-${uid})`;
  const leaf = `url(#leafG-${uid})`;

  const Bloom = ({ x, y, r = 30, rot = 0, opacity = 1 }) => (
    <g transform={`translate(${x} ${y}) rotate(${rot})`} opacity={opacity}>
      {/* 5 rounded petals radiating from center */}
      {[0, 72, 144, 216, 288].map((a, i) => (
        <ellipse
          key={i}
          cx="0"
          cy={-r * 0.55}
          rx={r * 0.55}
          ry={r * 0.75}
          fill={petal}
          transform={`rotate(${a})`}
        />
      ))}
      {/* deeper inner shading behind center */}
      {[36, 108, 180, 252, 324].map((a, i) => (
        <ellipse
          key={`d${i}`}
          cx="0"
          cy={-r * 0.35}
          rx={r * 0.22}
          ry={r * 0.45}
          fill={petalDeep}
          opacity="0.45"
          transform={`rotate(${a})`}
        />
      ))}
      {/* pale center */}
      <circle r={r * 0.28} fill="#F3E4F7" />
      {/* stamens — thin lines with tiny tips */}
      {[-40, -20, 0, 20, 40].map((a, i) => (
        <g key={`s${i}`} transform={`rotate(${a})`}>
          <line x1="0" y1="0" x2="0" y2={-r * 0.75} stroke="#4E2E75" strokeWidth="1" strokeLinecap="round" />
          <circle cx="0" cy={-r * 0.78} r="1.6" fill="#4E2E75" />
        </g>
      ))}
    </g>
  );

  return (
    <svg
      viewBox="0 0 200 460"
      className={className}
      style={{ transform: flip ? 'scaleX(-1)' : 'none' }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id={`petG-${uid}`} x1="0" x2="1" y1="0" y2="1">
          <stop offset="0" stopColor="#E9D3F0" />
          <stop offset="0.55" stopColor="#C8A6DA" />
          <stop offset="1" stopColor="#9E78BF" />
        </linearGradient>
        <linearGradient id={`petD-${uid}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#B88ACB" />
          <stop offset="1" stopColor="#6E4D95" />
        </linearGradient>
        <linearGradient id={`leafG-${uid}`} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0" stopColor="#C1D0A3" />
          <stop offset="1" stopColor="#7A8F5F" />
        </linearGradient>
      </defs>

      {/* sword-shaped leaves at base */}
      <path d="M92 460 C 30 400, 12 300, 46 170 C 60 280, 82 380, 96 460 Z" fill={leaf} opacity="0.95" />
      <path d="M100 460 C 152 400, 168 300, 138 180 C 128 280, 110 380, 100 460 Z" fill={leaf} opacity="0.85" />
      <path d="M96 460 Q 90 340 92 220" stroke="#7A8F5F" strokeWidth="1.6" fill="none" opacity="0.7" />

      {/* tall hyacinth-like bud spike on the left, mimicking the logo */}
      <g transform="translate(50 60)">
        {/* stem */}
        <path d="M6 340 C 4 240, 10 140, 6 20" stroke="#7A8F5F" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        {/* small buds paired along the stem */}
        {Array.from({ length: 10 }).map((_, i) => {
          const y = 30 + i * 30;
          const size = 5 + (i < 5 ? i * 0.6 : (10 - i) * 0.6);
          return (
            <g key={i}>
              <ellipse cx={-1} cy={y} rx={size} ry={size * 1.6} fill={petal} />
              <ellipse cx={13} cy={y + 6} rx={size} ry={size * 1.6} fill={petalDeep} opacity="0.75" />
              {/* subtle highlight */}
              <ellipse cx={-2} cy={y - size * 0.4} rx={size * 0.4} ry={size * 0.6} fill="#F3E4F7" opacity="0.6" />
            </g>
          );
        })}
        {/* small green sepals near buds */}
        {[60, 120, 180, 240].map((y, i) => (
          <path key={i} d={`M4 ${y + 6} q -6 4 -10 -2`} stroke="#7A8F5F" strokeWidth="1.4" fill="none" opacity="0.7" />
        ))}
      </g>

      {/* three face-on blooms, layered like the logo (largest lower, medium mid, small upper) */}
      <Bloom x="126" y="330" r="46" rot={-6} />
      <Bloom x="150" y="248" r="38" rot={10} />
      <Bloom x="122" y="176" r="30" rot={-14} opacity={0.95} />
    </svg>
  );
};

/* --- Friendly bee --- */
export const Bee = ({ className = '' }) => {
  const uid = React.useId().replace(/:/g, '');
  return (
  <svg viewBox="0 0 100 80" className={className} aria-hidden="true">
    <defs>
      <clipPath id={`beeBody-${uid}`}>
        <ellipse cx="50" cy="42" rx="26" ry="20" />
      </clipPath>
    </defs>
    {/* wings */}
    <ellipse cx="38" cy="22" rx="16" ry="10" fill="#E9DCF2" opacity="0.85" />
    <ellipse cx="58" cy="22" rx="16" ry="10" fill="#E9DCF2" opacity="0.85" />
    {/* body */}
    <ellipse cx="50" cy="42" rx="26" ry="20" fill="#F5C857" />
    {/* vertical stripes clipped to body */}
    <g clipPath={`url(#beeBody-${uid})`}>
      <rect x="36" y="20" width="6" height="44" fill="#24385C" />
      <rect x="50" y="20" width="6" height="44" fill="#24385C" />
    </g>
    {/* face */}
    <circle cx="70" cy="42" r="10" fill="#F7D06E" />
    <circle cx="66" cy="40" r="1.5" fill="#24385C" />
    <circle cx="74" cy="40" r="1.5" fill="#24385C" />
    <path d="M67 46 Q 70 48 73 46" stroke="#24385C" strokeWidth="1.4" fill="none" strokeLinecap="round" />
    <circle cx="63" cy="46" r="2" fill="#FBB6C6" opacity="0.7" />
    <circle cx="77" cy="46" r="2" fill="#FBB6C6" opacity="0.7" />
    {/* antennae */}
    <path d="M64 32 Q 62 22 58 20" stroke="#24385C" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    <path d="M74 32 Q 76 22 80 20" stroke="#24385C" strokeWidth="1.5" fill="none" strokeLinecap="round" />
    <circle cx="58" cy="20" r="2" fill="#24385C" />
    <circle cx="80" cy="20" r="2" fill="#24385C" />
  </svg>
  );
};

/* --- Little heart trail --- */
export const HeartTrail = ({ className = '' }) => (
  <svg viewBox="0 0 200 80" className={className} aria-hidden="true">
    <path d="M4 60 C 30 20, 60 90, 90 40 T 180 40" stroke="#B88ACB" strokeWidth="2" strokeDasharray="4 6" fill="none" strokeLinecap="round" />
    <path d="M180 40 c -2 -4 -8 -4 -8 1 c 0 -5 -6 -5 -8 -1 c -3 5 8 10 8 10 s 11 -5 8 -10 z" fill="#B88ACB" />
  </svg>
);

/* --- Botanical sprig (small leafy accent) --- */
export const Sprig = ({ className = '', flip = false }) => (
  <svg
    viewBox="0 0 120 60"
    className={className}
    style={{ transform: flip ? 'scaleX(-1)' : 'none' }}
    aria-hidden="true"
  >
    <path d="M6 30 Q 60 20 114 30" stroke="#A8B78C" strokeWidth="2" fill="none" strokeLinecap="round" />
    <path d="M30 28 q -6 -12 -14 -8 q 4 12 14 8 z" fill="#A8B78C" opacity="0.85" />
    <path d="M50 30 q -8 -14 -18 -6 q 6 14 18 6 z" fill="#A8B78C" opacity="0.9" />
    <path d="M74 30 q 8 -14 18 -6 q -6 14 -18 6 z" fill="#A8B78C" opacity="0.9" />
    <path d="M94 28 q 6 -12 14 -8 q -4 12 -14 8 z" fill="#A8B78C" opacity="0.85" />
  </svg>
);

/* --- Sun --- */
export const Sun = ({ className = '' }) => (
  <svg viewBox="0 0 80 80" className={className} aria-hidden="true">
    <circle cx="40" cy="40" r="18" fill="#F5C857" />
    {Array.from({ length: 12 }).map((_, i) => {
      const angle = (i * Math.PI * 2) / 12;
      const x1 = 40 + Math.cos(angle) * 24;
      const y1 = 40 + Math.sin(angle) * 24;
      const x2 = 40 + Math.cos(angle) * 34;
      const y2 = 40 + Math.sin(angle) * 34;
      return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#F5C857" strokeWidth="3" strokeLinecap="round" />;
    })}
  </svg>
);

/* --- Wavy divider --- */
export const WavyDivider = ({ fill = '#FBE8EE', className = '' }) => (
  <svg viewBox="0 0 1440 80" className={className} preserveAspectRatio="none" aria-hidden="true">
    <path d="M0 40 C 240 90, 480 -10, 720 40 C 960 90, 1200 -10, 1440 40 L 1440 80 L 0 80 Z" fill={fill} />
  </svg>
);

/* --- The Feelings Bench book cover (SVG placeholder illustration) --- */
export const FeelingsBenchCover = ({ className = '' }) => (
  <svg viewBox="0 0 320 420" className={className} aria-hidden="true">
    <defs>
      <linearGradient id="skyG" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#FBE8EE" />
        <stop offset="1" stopColor="#FBF7F2" />
      </linearGradient>
      <linearGradient id="benchG" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#B88ACB" />
        <stop offset="1" stopColor="#8D68B2" />
      </linearGradient>
    </defs>
    {/* cover */}
    <rect x="0" y="0" width="320" height="420" rx="14" fill="url(#skyG)" />
    {/* sun */}
    <circle cx="250" cy="80" r="34" fill="#F5C857" opacity="0.9" />
    <circle cx="250" cy="80" r="46" fill="#F5C857" opacity="0.25" />
    {/* rolling hill */}
    <path d="M0 300 C 80 260, 240 260, 320 300 L 320 420 L 0 420 Z" fill="#A8B78C" />
    <path d="M0 320 C 80 290, 240 290, 320 320 L 320 420 L 0 420 Z" fill="#8D9E70" opacity="0.6" />
    {/* small flowers */}
    <g>
      {[30, 70, 110, 200, 260].map((cx, i) => (
        <g key={i} transform={`translate(${cx} ${320 + (i % 2) * 8})`}>
          <line x1="0" y1="0" x2="0" y2="18" stroke="#7C8F65" strokeWidth="2" />
          <circle cx="0" cy="-3" r="4" fill="#B88ACB" />
        </g>
      ))}
    </g>
    {/* bench */}
    <g transform="translate(90 250)">
      <rect x="0" y="20" width="140" height="10" rx="4" fill="url(#benchG)" />
      <rect x="0" y="34" width="140" height="6" rx="3" fill="#8D68B2" />
      <rect x="10" y="40" width="6" height="34" fill="#8D68B2" />
      <rect x="124" y="40" width="6" height="34" fill="#8D68B2" />
    </g>
    {/* child sitting */}
    <g transform="translate(150 210)">
      <circle cx="0" cy="0" r="14" fill="#F5C857" />
      <circle cx="-4" cy="-1" r="1.5" fill="#24385C" />
      <circle cx="4" cy="-1" r="1.5" fill="#24385C" />
      <path d="M-4 5 Q 0 8 4 5" stroke="#24385C" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M-12 12 Q 0 30 12 12 L 14 42 L -14 42 Z" fill="#8D68B2" />
    </g>
    {/* bee */}
    <g transform="translate(60 140)">
      <defs>
        <clipPath id="miniBeeBody">
          <ellipse cx="0" cy="0" rx="10" ry="7" />
        </clipPath>
      </defs>
      <ellipse cx="0" cy="0" rx="10" ry="7" fill="#F5C857" />
      <g clipPath="url(#miniBeeBody)">
        <rect x="-6" y="-8" width="3" height="16" fill="#24385C" />
        <rect x="1" y="-8" width="3" height="16" fill="#24385C" />
      </g>
      <ellipse cx="-6" cy="-6" rx="6" ry="4" fill="#E9DCF2" opacity="0.9" />
      <ellipse cx="6" cy="-6" rx="6" ry="4" fill="#E9DCF2" opacity="0.9" />
    </g>
    <path d="M70 145 Q 100 130 140 160" stroke="#B88ACB" strokeWidth="1.5" strokeDasharray="3 4" fill="none" />

    {/* title band */}
    <rect x="20" y="24" width="280" height="82" rx="10" fill="#FBF7F2" opacity="0.85" />
    <text x="160" y="60" textAnchor="middle" fontFamily="Fraunces, serif" fontWeight="700" fontSize="30" fill="#24385C">
      The Feelings
    </text>
    <text x="160" y="94" textAnchor="middle" fontFamily="Fraunces, serif" fontWeight="700" fontSize="30" fill="#24385C">
      Bench
    </text>
    <text x="160" y="400" textAnchor="middle" fontFamily="Caveat, cursive" fontSize="20" fill="#24385C">
      by Erica Thayer
    </text>
  </svg>
);
